import { GST_STATUS, PRIMARY_GST_STATUSES } from '../investor/constants';
import { GST_STATE_BY_CODE } from './constants';
import type {
    AionGstListLeg,
    DataAvailability,
    DataAvailabilityConnection,
    DataAvailabilityGstRow,
    DataAvailabilityResponseBody,
    MonthFlags,
    UwDataAvailability,
} from './types';

const MONTHS_PER_WINDOW = 12;

const NO_AUDITED = { available: false, fys: [] as number[] };

const isTwelveMonths = (months: unknown): months is string[] =>
    Array.isArray(months) && months.length === MONTHS_PER_WINDOW && months.every((month) => typeof month === 'string');

const allMonthsFalse = (months: string[]): MonthFlags => Object.fromEntries(months.map((month) => [month, false]));

// Same precedence as DataSource.tsx: connected wins, then an expired registration reads
// "Disconnected", everything else (including an absent `connected`) is "Not Connected".
const connectionOf = (gst: NonNullable<AionGstListLeg['totalGstList']>[number]): DataAvailabilityConnection => {
    if (gst.connected === true) return 'CONNECTED';
    if ((gst.gstStatus ?? '').toUpperCase() === GST_STATUS.EXPIRED) return 'DISCONNECTED';
    return 'NOT_CONNECTED';
};

/**
 * Merges the polus envelope's two legs into what the modal renders: all of the roster's GSTINs are kept
 * (primary — the popup's connected group — first, then others) and UW-only GSTINs are dropped.
 * Returns null when either leg is missing or malformed: polus maps a failed leg to `{}` inside HTTP 200, and without the UW
 * months every roster GSTIN would falsely read Not Connected. An empty `totalGstList`
 * is a genuine answer, not a failure.
 */
export function mergeAvailability(responseBody: DataAvailabilityResponseBody | null | undefined): DataAvailability | null {
    const uw: UwDataAvailability | undefined = responseBody?.UW_GET_DATA_AVAILABILITY?.data?.data;
    if (!uw || !isTwelveMonths(uw.months)) return null;
    const roster = responseBody?.AION_GET_GST_LIST_BY_REQUESTED_ORG?.totalGstList;
    if (!Array.isArray(roster)) return null;

    const { months } = uw;
    const uwMonthsByGstin = new Map((uw.gst ?? []).map((row) => [row.gstin, row.months]));
    const seen = new Set<string>();
    const primary: DataAvailabilityGstRow[] = [];
    const others: DataAvailabilityGstRow[] = [];

    roster.forEach((entry) => {
        const gstin = entry?.gstNumber;
        if (!gstin || seen.has(gstin)) return;
        seen.add(gstin);
        const status = (entry.gstStatus ?? '').toUpperCase();
        const isPrimary = PRIMARY_GST_STATUSES.includes(status);
        (isPrimary ? primary : others).push({
            gstin,
            state: entry.state || GST_STATE_BY_CODE[gstin.slice(0, 2)] || '',
            status,
            isPrimary,
            connection: isPrimary ? connectionOf(entry) : null,
            months: uwMonthsByGstin.get(gstin) ?? allMonthsFalse(months),
        });
    });
    const gst = [...primary, ...others];

    return {
        fy: uw.fy ?? null,
        window: uw.window ?? null,
        months,
        gst,
        bank: uw.bank ?? [],
        audited: uw.audited ?? NO_AUDITED,
        provisional: uw.provisional ?? NO_AUDITED,
    };
}
